import { FieldFormat, SectionConfig } from "../../types";
import { selectTargetStep } from "../selectors";
import { buildOwnLabel } from "./helpers";

/**
 * `bidirectional` is a boolean in the rule, but the form edits it through a
 * two-option select, so it reads as those options and not as on/off.
 * See SelectDirection.tsx.
 */
const FLOW_DIRECTION: FieldFormat = {
  enum: {
    true: "emailFlowSection.between",
    false: "emailFlowSection.from",
  },
};

/**
 * Several flags answer the same question — is this done once for everyone, or
 * once per recipient — so they share one pair of values instead of repeating
 * the long sentences the form uses to describe each option.
 */
const RECIPIENT_SCOPE: FieldFormat = {
  enum: {
    true: buildOwnLabel("recipientScope.shared"),
    false: buildOwnLabel("recipientScope.perRecipient"),
  },
};

/** Same values, for a flag whose name already means "one per recipient". */
const RECIPIENT_SCOPE_INVERTED: FieldFormat = {
  enum: {
    true: buildOwnLabel("recipientScope.perRecipient"),
    false: buildOwnLabel("recipientScope.shared"),
  },
};

const IMAP_AUTH: FieldFormat = {
  enum: {
    PLAIN: "imapConfig.authOption.PLAIN",
    LOGIN: "imapConfig.authOption.LOGIN",
  },
};

const config: SectionConfig = {
  section: "targetStep",
  titleKey: "confirmRuleUpdates.targetStep.title",
  selector: selectTargetStep,
  properties: {
    bidirectional: {
      label: "confirmRuleUpdates.emailFlow.bidirectional",
      format: FLOW_DIRECTION,
    },
    evaluateHeaders: {
      label: "confirmRuleUpdates.emailFlow.evaluateHeaders",
      format: "boolean",
    },
    evaluateFrom: {
      label: "confirmRuleUpdates.emailFlow.evaluateFrom",
      format: "boolean",
    },
    smtpPhase: { label: buildOwnLabel("smtpPhase"), format: RECIPIENT_SCOPE },
    fromDirection: {
      label: "confirmRuleUpdates.emailFlow.sender",
      format: { resolve: "direction" },
    },
    toDirection: {
      label: "confirmRuleUpdates.emailFlow.recipient",
      format: { resolve: "direction" },
    },
    syncAllByRule: {
      label: buildOwnLabel("syncAllByRule"),
      format: RECIPIENT_SCOPE,
    },
    passwordPerRecipient: {
      label: buildOwnLabel("passwordPerRecipient"),
      format: RECIPIENT_SCOPE_INVERTED,
    },
    saveForwardedMessages: {
      label: "confirmRuleUpdates.archiving.saveForwardedMessages",
      format: "boolean",
    },
    emailAddress: { label: "confirmRuleUpdates.archiving.emailAddress" },
    redirectOnErrorEmailAddress: {
      label: "confirmRuleUpdates.archiving.redirectOnErrorEmailAddress",
    },
    imapHost: { label: "imapConfig.host" },
    imapUsername: { label: "imapConfig.account" },
    imapPassword: { label: "imapConfig.password", format: "password" },
    imapPort: { label: "imapConfig.port" },
    imapSsl: { label: "imapConfig.useSSL", format: "boolean" },
    imapAuthMechanism: { label: "imapConfig.authType", format: IMAP_AUTH },
    notificationOnDone: {
      label: "mailboxSection.onDone.label",
      format: "boolean",
    },
    notificationOnError: {
      label: "mailboxSection.onError.label",
      format: "boolean",
    },
    notificationEmail: {
      label: "confirmRuleUpdates.mailbox.notificationEmail",
    },
    postProcessingMarkProcessed: {
      label: "mailboxSection.markProcessed.label",
      format: "boolean",
    },
    postProcessingMarkProcessedText: {
      label: "confirmRuleUpdates.mailbox.markProcessedText",
    },
    copyEmailFilesWhenNoAttachments: {
      label: "mailboxSection.copyFiles.label",
      format: "boolean",
    },
    folders: {
      label: "confirmRuleUpdates.storageWebhook.folders",
      format: "folder",
      getItemKey: item => (item as { id: string }).id,
    },
  },
};

export default config;
